import React, { useState, useEffect, useRef } from 'react';
import { Carousel, Row, Col, Typography, Button, Card } from 'antd';
import { LeftOutlined, RightOutlined, StarFilled } from '@ant-design/icons';
import ReviewCard from './ReviewCard';
import ReviewsSkeleton from './ReviewsSkeleton';
import ReviewsError from './ReviewsError';
import { CustomerReviewsProps, ReviewData } from '../../@types/review';

const { Title, Paragraph } = Typography;

const CustomerReviews: React.FC<CustomerReviewsProps> = ({
    reviews,
    title = "รีวิวจากลูกค้า",
    autoPlay = true,
    autoPlaySpeed = 4000,
    loading = false,
    error = false,
    onRetry
}) => {
    const carouselRef = useRef<any>(null);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [cardsPerView, setCardsPerView] = useState(5);

    const getCardsPerView = () => {
        if (typeof window !== 'undefined') {
            if (window.innerWidth >= 1024) return 3; // Desktop: 3 cards
            if (window.innerWidth >= 768) return 2;  // Tablet: 2 cards  
            return 1; // Mobile: 1 card
        }
        return 3;
    };

    useEffect(() => {
        const handleResize = () => {
            setCardsPerView(getCardsPerView());
        };

        // Set initial value
        setCardsPerView(getCardsPerView());

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // จัดกลุ่ม reviews ตาม cardsPerView
    const groupedReviews: ReviewData[][] = [];
    for (let i = 0; i < reviews.length; i += cardsPerView) {
        groupedReviews.push(reviews.slice(i, i + cardsPerView));
    }

    const totalSlides = groupedReviews.length;

    // Responsive settings for Ant Design Carousel
    const getResponsiveSettings = () => {
        return {
            dots: false,
            infinite: totalSlides > 1,
            autoplay: autoPlay && totalSlides > 1,
            autoplaySpeed: autoPlaySpeed,
            speed: 500,
            slidesToShow: 1,
            slidesToScroll: 1,
            arrows: false,
            beforeChange: (current: number, next: number) => setCurrentSlide(next),
        };
    };

    // Loading state
    if (loading) {
        return (
            <section className="review-section">
                <div className="section-container">
                    <div className="section-header">
                        <div className="header-stars">
                            <StarFilled className="star-icon" />
                            <Title level={2} className="section-title">
                                {title}
                            </Title>
                            <StarFilled className="star-icon" />
                        </div>
                        <Paragraph className="section-description">
                            ความคิดเห็นและประสบการณ์จากลูกค้าที่ใช้บริการของเรา
                        </Paragraph>
                    </div>
                    <ReviewsSkeleton count={cardsPerView} />
                </div>
            </section>
        );
    }

    // // Error state
    // if (error && onRetry) {
    //     return (
    //         <section className="review-section">
    //             <div className="section-container">
    //                 <ReviewsError onRetry={onRetry} />
    //             </div>
    //         </section>
    //     );
    // }

    // Empty state
    if (!reviews || reviews.length === 0) {
        return (
            <section className="review-section">
                {/* <div className="section-container empty-state">
                    <Title level={3} className="empty-title">
                        ยังไม่มีรีวิวจากลูกค้า
                    </Title>
                    <Paragraph className="empty-description">
                        เป็นคนแรกที่ให้รีวิวเกี่ยวกับบริการของเรา
                    </Paragraph>
                </div> */}
            </section>
        );
    }

    // const getColSpan = (index: number) => {
    //     if (typeof window !== 'undefined') {
    //         if (window.innerWidth >= 1024) return 8;  // Desktop: 3 cards (24/3 = 8)
    //         if (window.innerWidth >= 768) return 12; // Tablet: 2 cards (24/2 = 12)
    //         return 24; // Mobile: 1 card (24/1 = 24)
    //     }
    //     return 8;
    // };

    return (
        <section className="review-section">
            <div className="section-container">
                {/* Header */}
                <div className="section-header">
                    <div className="header-stars">
                        <StarFilled className="star-icon" />
                        <Title level={2} className="section-title">
                            {title}
                        </Title>
                        <StarFilled className="star-icon" />
                    </div>
                    <Paragraph className="section-description">
                        ความคิดเห็นและประสบการณ์จากลูกค้าที่ใช้บริการของเรา
                    </Paragraph>
                </div>

                {/* Reviews Carousel */}
                <div className="carousel-container">
                    <Carousel
                        ref={carouselRef}
                        {...getResponsiveSettings()}
                        className="reviews-carousel"
                    >
                        {groupedReviews.map((reviewGroup, slideIndex) => (
                            <div key={slideIndex}>
                                <Row gutter={[16, 16]} className="review-row">
                                    {reviewGroup.map((review) => (
                                        <Col
                                            key={review.id}
                                            xs={24} // Mobile: 1 card (24/24 = 1)
                                            md={12} // Tablet: 2 cards (12/24 = 1/2)
                                            lg={8}  // Desktop: 3 cards (8/24 = 1/3)
                                        >
                                            <ReviewCard review={review} />
                                        </Col>
                                    ))}
                                </Row>
                            </div>
                        ))}
                    </Carousel>

                    {/* Custom Navigation Buttons */}
                    {/* {totalSlides > 1 && (
                        <>
                            <Button
                                type="primary"
                                shape="circle"
                                icon={<LeftOutlined />}
                                onClick={() => carouselRef.current?.prev()}
                                className="carousel-nav-btn nav-left"
                                size="large"
                            />
                            <Button
                                type="primary"
                                shape="circle"
                                icon={<RightOutlined />}
                                onClick={() => carouselRef.current?.next()}
                                className="carousel-nav-btn nav-right"
                                size="large"
                            />
                        </>
                    )} */}
                </div>

                {/* Slide Indicators */}
                {totalSlides > 1 && (
                    <div className="slide-indicators">
                        {Array.from({ length: totalSlides }).map((_, index) => (
                            <button
                                key={index}
                                onClick={() => carouselRef.current?.goTo(index)}
                                className={`indicator ${index === currentSlide ? 'active' : ''}`}
                            />
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
};

export default CustomerReviews; 